import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useCandidateProfile, useUpdateCandidateProfile } from './queries';
import { CandidateProfileFormFields } from './CandidateProfileForm';
import { formatExperience } from '@/lib/format';
import { apiErrorMessage } from '@/lib/errors';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ErrorState, LoadingState } from '@/components/common/States';
import { toast } from '@/components/ui/toast';

export function CandidateProfilePage() {
  const { t } = useTranslation('candidate');
  usePageTitle(t('profile.title'));
  const { data: profile, isLoading, isError, refetch } = useCandidateProfile();
  const update = useUpdateCandidateProfile();
  const [editing, setEditing] = useState(false);

  if (isLoading) return <LoadingState />;
  if (isError || !profile) return <ErrorState onRetry={refetch} />;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1>{t('profile.title')}</h1>
        {!editing && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            {t('profile.edit')}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-lg">{t('profile.sections.basic')}</h2>
        </CardHeader>
        <CardBody>
          {editing ? (
            <CandidateProfileFormFields
              initial={profile}
              submitLabel={t('actions.save', { ns: 'common', defaultValue: 'Save' })}
              submitting={update.isPending}
              onSubmit={(input) =>
                update.mutate(input, {
                  onSuccess: () => {
                    toast.success(t('profile.saved'));
                    setEditing(false);
                  },
                  onError: (err) => toast.error(apiErrorMessage(err)),
                })
              }
            />
          ) : (
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <Item label={t('fields.fullName')} value={profile.fullName} />
              <Item label={t('fields.phone')} value={profile.phone} />
              <Item label={t('fields.email')} value={profile.email} />
              <Item
                label={t('fields.gender')}
                value={profile.gender ? t(`fields.genderOptions.${profile.gender}`) : null}
              />
              <Item label={t('fields.highestEducation')} value={profile.highestEducation} />
              <Item label={t('fields.itiTrade')} value={profile.itiTrade} />
              <Item label={t('fields.itiCollege')} value={profile.itiCollege} />
              <Item label={t('fields.department')} value={profile.department} />
              <Item
                label={t('fields.workExperienceMonths')}
                value={formatExperience(profile.workExperienceMonths)}
              />
              <Item label={t('fields.city')} value={profile.city} />
              <Item label={t('fields.district')} value={profile.district} />
              <Item label={t('fields.pincode')} value={profile.pincode} />
              {profile.description && (
                <div className="col-span-full">
                  <dt className="text-sm text-content-muted">{t('fields.description')}</dt>
                  <dd className="mt-1 whitespace-pre-line font-medium">{profile.description}</dd>
                </div>
              )}
              {profile.skills && profile.skills.length > 0 && (
                <div className="col-span-full">
                  <dt className="text-sm text-content-muted">{t('fields.skills')}</dt>
                  <dd className="mt-1 flex flex-wrap gap-2">
                    {profile.skills.map((s) => (
                      <span key={s} className="rounded-full bg-brand-50 px-3 py-1 text-sm text-brand-800">
                        {s}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
              {profile.workExperiences && profile.workExperiences.length > 0 && (
                <div className="col-span-full">
                  <dt className="text-sm text-content-muted">{t('profile.sections.workHistory')}</dt>
                  <dd className="mt-1 flex flex-col gap-3">
                    {profile.workExperiences.map((w, i) => (
                      <div key={i}>
                        <div className="font-medium">{[w.role, w.company].filter(Boolean).join(' · ')}</div>
                        {(w.fromMonth || w.toMonth || w.current) && (
                          <div className="text-xs text-content-muted">
                            {[w.fromMonth, w.current ? t('fields.workExp.present') : w.toMonth]
                              .filter(Boolean)
                              .join(' – ')}
                          </div>
                        )}
                        {w.description && <div className="mt-0.5 whitespace-pre-line text-sm">{w.description}</div>}
                      </div>
                    ))}
                  </dd>
                </div>
              )}
              {profile.educations && profile.educations.length > 0 && (
                <div className="col-span-full">
                  <dt className="text-sm text-content-muted">{t('profile.sections.educationHistory')}</dt>
                  <dd className="mt-1 flex flex-col gap-2">
                    {profile.educations.map((ed, i) => (
                      <div key={i} className="text-sm">
                        <span className="font-medium">{[ed.degree, ed.field].filter(Boolean).join(', ')}</span>
                        {ed.institution && <span> — {ed.institution}</span>}
                        {ed.year && <span className="text-content-muted"> ({ed.year})</span>}
                      </div>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Item({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="min-w-0">
      <dt className="text-sm text-content-muted">{label}</dt>
      <dd className="break-words font-medium">{value || '—'}</dd>
    </div>
  );
}
